import {
  Box,
  Divider,
  Flex,
  Form,
  MonetaryInputField,
  SubmitButton,
  Text,
} from '@/components';
import { PreviousFiscalYearExecutedFormSchema } from '@/features/project/projects/_models/segment-expenditure-and-execution';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof PreviousFiscalYearExecutedFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const PreviousFiscalYearExecutedForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(PreviousFiscalYearExecutedFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex vertical gap={16}>
        <Text variant="h5" text={t('pre-year-ordinary-revenue')} />
        <MonetaryInputField
          name="revenueManagementExpenseExecutedAmount"
          label={t('management-expense-grant')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueSubsidyExecutedAmount"
          label={t('subsidy-money')}
          control={control}
          errors={errors}
          unit={unit}
        />
        <MonetaryInputField
          name="revenueOthersExecutedAmount"
          label={t('others')}
          control={control}
          errors={errors}
          unit={unit}
        />

        <Divider style={{ borderWidth: '2px', marginTop: 0 }} />
        <MonetaryInputField
          labelSize="large"
          name="expenditureExecutedAmount"
          label={t('pre-year-ordinary-expenses')}
          control={control}
          errors={errors}
          unit={unit}
        />
      </Flex>
      <Box marginTop={12}>
        <SubmitButton
          text={t('save')}
          loading={isSubmitting}
          disabled={!isDirty}
        />
      </Box>
    </Form>
  );
};
